import { loadAssistantOpen, saveAssistantOpen } from "ai-assistant/dock/assistant-dock-preference";
import React from "react";

/** The docked panel's id, which each launcher names in `aria-controls`. */
export const ASSISTANT_PANEL_ID = "ai-assistant-panel";

/** Marks a control that opens the assistant, so focus can be handed back to whichever one is drawn. */
export const ASSISTANT_LAUNCHER_ATTRIBUTE = "data-assistant-launcher";

/**
 * What a launcher says, on screen and to assistive technology alike.
 *
 * One string for both, so that a user of voice control can say the words they see (WCAG 2.5.3). The
 * floating button's name was "Chat with AI Assistant" over the visible word "Assistant".
 */
export const ASSISTANT_LAUNCHER_LABEL = "Ask the assistant";

type AssistantDock = {
  isOpen: boolean;
  /**
   * Opens the assistant. `returnFocusTo` is where the keyboard goes when it closes again; left out,
   * it is whatever held focus when it opened.
   */
  open: (returnFocusTo?: HTMLElement | null) => void;
  close: () => void;
  toggle: (returnFocusTo?: HTMLElement | null) => void;
  /** Where focus should go now that the assistant has closed, taken once. */
  takeReturnFocusTarget: () => HTMLElement | null;
  /** The keys that open it on this platform, in `aria-keyshortcuts` form. */
  keyShortcuts: string;
  /** The same keys, as a label a person reads. */
  shortcutLabel: string;
};

const AssistantDockContext = React.createContext<AssistantDock | undefined>(undefined);

/**
 * Whether the shortcut is ⌘K rather than Ctrl+K here.
 *
 * Only ⌘ on a Mac: Ctrl+K there is the text field's "delete to the end of the line", which a user
 * typing in any field on the page means literally.
 */
export function isApplePlatform(): boolean {
  const navigatorLike = globalThis.navigator as (Navigator & { userAgentData?: { platform?: string } }) | undefined;
  const platform = navigatorLike?.userAgentData?.platform ?? navigatorLike?.platform ?? "";
  return /mac|iphone|ipad/i.test(platform);
}

/**
 * Whether a modal has put the application behind it.
 *
 * `enableInertModalHiding` makes `#root` inert while a dialog or a modal drawer is open. The
 * launchers are inside it and cannot be reached then, so the shortcut must not reach past the
 * dialog either: the dialog's focus trap would take the keyboard straight back.
 */
function isApplicationBehindAModal(): boolean {
  const root = globalThis.document?.getElementById("root");
  return Boolean(root?.closest("[inert]"));
}

type AssistantDockProviderProps = {
  /** Whose choice to keep it open this is. Without one, nothing is remembered. */
  userId?: string;
  children: React.ReactNode;
};

/**
 * Whether the assistant is open, for the shell that docks it and the controls that open it.
 *
 * The choice is remembered per user across reloads (#6128). Keyed on the user by the caller, so a
 * different user signing in starts from their own choice rather than the last one's.
 */
export function AssistantDockProvider(props: Readonly<AssistantDockProviderProps>) {
  const { userId, children } = props;
  const [isOpen, setIsOpen] = React.useState<boolean>(() => loadAssistantOpen(userId));
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const isApple = isApplePlatform();

  const setOpen = React.useCallback((next: boolean) => {
    setIsOpen(next);
    saveAssistantOpen(userId, next);
  }, [userId]);

  const open = React.useCallback((returnFocusTo?: HTMLElement | null) => {
    const focused = globalThis.document?.activeElement;
    returnFocusRef.current = returnFocusTo ?? (focused instanceof HTMLElement ? focused : null);
    setOpen(true);
  }, [setOpen]);

  const close = React.useCallback(() => setOpen(false), [setOpen]);

  const toggle = React.useCallback((returnFocusTo?: HTMLElement | null) => {
    if (isOpen) {
      close();
      return;
    }
    open(returnFocusTo);
  }, [close, isOpen, open]);

  const takeReturnFocusTarget = React.useCallback(() => {
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    return target;
  }, []);

  React.useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.altKey || event.shiftKey) return;
      if (event.key.toLowerCase() !== "k") return;

      const modifierHeld = isApple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
      if (!modifierHeld || isApplicationBehindAModal()) return;

      // The browser's own Ctrl+K is "search from the address bar", which is not what someone
      // pressing it inside the app is asking for.
      event.preventDefault();
      toggle();
    }

    globalThis.document?.addEventListener("keydown", handleKeyDown);
    return () => globalThis.document?.removeEventListener("keydown", handleKeyDown);
  }, [isApple, toggle]);

  const value = React.useMemo<AssistantDock>(() => ({
    isOpen,
    open,
    close,
    toggle,
    takeReturnFocusTarget,
    keyShortcuts: isApple ? "Meta+K" : "Control+K",
    shortcutLabel: isApple ? "⌘K" : "Ctrl K",
  }), [close, isApple, isOpen, open, takeReturnFocusTarget, toggle]);

  return <AssistantDockContext.Provider value={value}>{children}</AssistantDockContext.Provider>;
}

export function useAssistantDock(): AssistantDock {
  const dock = React.useContext(AssistantDockContext);
  if (dock === undefined) {
    throw new Error("useAssistantDock must be used inside an AssistantDockProvider.");
  }
  return dock;
}
